import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { CATEGORIES } from "../lib/api";

const NAV = [
    ...CATEGORIES.map((c) => ({
        name: c.short,
        to: `/category/${c.slug}`,
    })),
    { name: "Contact", to: "/contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "py-3 glass-strong border-b border-gold/20 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]"
                    : "py-5 bg-gradient-to-b from-ink/70 via-ink/30 to-transparent backdrop-blur-sm"
            }`}
        >
            <div className="max-w-[1480px] mx-auto px-5 md:px-8 flex items-center justify-between gap-4">
                <Link
                    to="/"
                    data-testid="header-logo"
                    className="flex items-center gap-3 group shrink-0"
                    aria-label="CrLuys Lifestyle — Home"
                >
                    <span className="relative inline-flex h-10 w-10 md:h-11 md:w-11 items-center justify-center rounded-full transition-all duration-500 group-hover:scale-[1.06]">
                        <span className="absolute inset-0 rounded-full ring-1 ring-gold/30 group-hover:ring-gold/70 transition-all" />
                        <span className="absolute -inset-1 rounded-full bg-gold/0 group-hover:bg-gold/15 blur-md transition-all duration-500" />
                        <img
                            src="/crluys-icon.webp?v=4"
                            alt="CrLuys Lifestyle"
                            className="brand-icon relative h-[2.625rem] w-[2.625rem] md:h-12 md:w-12 object-contain select-none"
                            draggable={false}
                        />
                    </span>
                    <span className="hidden sm:flex flex-col leading-tight">
                        <span className="font-serif text-[15px] md:text-base text-cream tracking-wide">
                            CrLuys
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.4em] text-gold/85 -mt-0.5">
                            Lifestyle
                        </span>
                    </span>
                </Link>

                <nav className="hidden xl:flex items-center gap-0 2xl:gap-1 min-w-0">
                    {NAV.map((n) => {
                        const testid = `nav-${n.name.replace(/\s+/g, "-").toLowerCase()}`;
                        const itemBase = `relative px-2 2xl:px-3 py-2 text-[10px] 2xl:text-[11px] tracking-[0.18em] 2xl:tracking-[0.22em] uppercase transition-all duration-300 whitespace-nowrap group/nav`;
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={testid}
                                className={({ isActive }) =>
                                    `${itemBase} ${
                                        isActive ? "text-gold" : "text-cream/70 hover:text-cream"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        <span className="relative">{n.name}</span>
                                        <span
                                            className={`absolute left-2 right-2 2xl:left-3 2xl:right-3 bottom-1 h-px bg-gold transition-transform duration-500 origin-left ${
                                                isActive
                                                    ? "scale-x-100 opacity-100"
                                                    : "scale-x-0 group-hover/nav:scale-x-100 opacity-70"
                                            }`}
                                            aria-hidden
                                        />
                                    </>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="hidden xl:flex items-center gap-2 2xl:gap-3 shrink-0">
                    <Link
                        to="/?focus=search"
                        data-testid="header-search-cta"
                        className="inline-flex items-center gap-1.5 px-2 2xl:px-3 py-2 rounded-full text-cream/65 hover:text-gold hover:bg-gold/5 transition-all duration-300 text-[10px] 2xl:text-[11px] tracking-[0.18em] 2xl:tracking-[0.22em] uppercase"
                    >
                        <Search className="h-3.5 w-3.5" /> Search
                    </Link>
                    <span className="h-4 w-px bg-gold/20" aria-hidden />
                    <Link
                        to="/admin/login"
                        data-testid="header-admin-link"
                        className="text-[10px] uppercase tracking-[0.28em] 2xl:tracking-[0.35em] text-cream/45 hover:text-gold transition-colors"
                    >
                        Admin
                    </Link>
                </div>

                <button
                    type="button"
                    aria-label="Menu"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((s) => !s)}
                    className="xl:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-cream"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="xl:hidden mt-4 mx-4 rounded-2xl glass-strong border border-gold/20 p-6 flex flex-col gap-4 animate-fade-up"
                >
                    {NAV.map((n) => {
                        const testid = `mobile-nav-${n.name.replace(/\s+/g, "-").toLowerCase()}`;
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={testid}
                                className={({ isActive }) =>
                                    `text-base tracking-wide ${
                                        isActive ? "text-gold" : "text-cream/85"
                                    }`
                                }
                            >
                                {n.name}
                            </NavLink>
                        );
                    })}
                    <Link
                        to="/admin/login"
                        data-testid="mobile-nav-admin"
                        className="text-sm text-cream/50 uppercase tracking-[0.25em]"
                    >
                        Admin
                    </Link>
                </div>
            )}
        </header>
    );
}
